import React from 'react'
import { Table, Button, Tag, Dropdown, Badge } from 'antd'
import { MoreOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Merchant } from '../types/merchant.types'

interface MerchantsTableProps {
    merchants: Merchant[]
    loading?: boolean
    pagination?: {
        current: number
        pageSize: number
        total: number
        onChange: (page: number, pageSize: number) => void
    }
    onRowClick: (merchant: Merchant) => void
    onVerifyKyc: (merchant: Merchant) => void
    onReject: (merchant: Merchant) => void
    onUnblock: (merchant: Merchant) => void
    selectedRowKeys?: React.Key[]
    onSelectionChange?: (selectedRowKeys: React.Key[]) => void
    canVerifyKyc?: boolean
}

export const MerchantsTable: React.FC<MerchantsTableProps> = ({
    merchants,
    loading,
    pagination,
    onRowClick,
    onVerifyKyc,
    onReject,
    onUnblock,
    selectedRowKeys,
    onSelectionChange,
    canVerifyKyc = true
}) => {
    const columns: ColumnsType<Merchant> = [
        {
            title: 'Business Name',
            dataIndex: 'business_name',
            key: 'business_name',
            sorter: (a, b) => a.business_name.localeCompare(b.business_name),
            render: (text, record) => (
                <div>
                    <div>{text}</div>
                    {record.created_by && <Tag color="purple" style={{ marginTop: 4, fontSize: '10px' }}>Admin Created</Tag>}
                </div>
            )
        },
        {
            title: 'Owner',
            dataIndex: 'owner_name',
            key: 'owner_name',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colors = {
                    onboarding_pending: 'orange',
                    pending: 'orange',
                    active: 'green',
                    verified: 'cyan',
                    rejected: 'red',
                    suspended: 'gray',
                    blocked: 'black'
                }
                const display = status === 'onboarding_pending' ? 'ONBOARDING' : status.toUpperCase()
                return <Tag color={colors[status as keyof typeof colors] || 'default'}>{display}</Tag>
            },
        },
        {
            title: 'KYC',
            dataIndex: 'kyc_status',
            key: 'kyc_status',
            render: (status: string) => {
                return <Badge status={status === 'approved' ? 'success' : 'processing'} text={status?.toUpperCase() || 'N/A'} />
            },
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const items = [
                    {
                        key: 'view',
                        icon: <EyeOutlined />,
                        label: 'View Details',
                        onClick: () => onRowClick(record),
                    },
                    ...(canVerifyKyc && record.status === 'pending' ? [{
                        key: 'verify',
                        icon: <CheckCircleOutlined />,
                        label: 'Verify KYC',
                        onClick: () => onVerifyKyc(record),
                    }, {
                        key: 'reject',
                        icon: <CloseCircleOutlined />,
                        label: 'Reject',
                        danger: true,
                        onClick: () => onReject(record),
                    }] : []),
                    ...(canVerifyKyc && record.status === 'blocked' ? [{
                        key: 'unblock',
                        icon: <CheckCircleOutlined />,
                        label: 'Unblock',
                        onClick: () => onUnblock(record),
                    }] : [])
                ]

                return (
                    <Dropdown menu={{ items }}>
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                )
            },
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={merchants}
            rowKey="merchant_id"
            loading={loading}
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            rowSelection={
                onSelectionChange
                    ? {
                        selectedRowKeys,
                        onChange: onSelectionChange,
                    }
                    : undefined
            }
            pagination={pagination ? {
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onChange: pagination.onChange,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} merchants`,
            } : {
                total: merchants.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} merchants`,
            }}
        />
    )
}
